import { Link } from "react-router-dom";
import type { CSSProperties } from "react";
import { prefetchMerchantRoute } from "./merchantRoutePrefetch";
import type { MerchantNavigationBadge } from "./useMerchantNavigationBadges";
import type { MerchantNavigationItem } from "./merchantNavigation";
import { useSupportNavigationTarget } from "../../../features/admin/support-navigation/SupportNavigation";

interface MerchantNavKeyProps {
  item: MerchantNavigationItem;
  label: string;
  active: boolean;
  badge?: MerchantNavigationBadge;
  orientation?: "horizontal" | "vertical" | "mobile";
  compact?: boolean;
  onNavigate?: () => void;
}

export function merchantBadgeText(badge?: MerchantNavigationBadge): string {
  const count = badge?.count;
  if (badge?.dot || !Number.isFinite(count) || !count || count < 1) return "";
  return count > 999 ? "999+" : String(Math.floor(count));
}

export function merchantBadgeStyle(badge?: MerchantNavigationBadge): CSSProperties {
  const text = merchantBadgeText(badge);
  return { "--merchant-badge-width": `${Math.max(20, text.length * 6 + 8)}px` } as CSSProperties;
}

export function MerchantBadge({ badge }: { badge?: MerchantNavigationBadge }) {
  if (!badge) return null;
  if (badge.dot) {
    return (
      <span
        className={`merchant-nav-badge merchant-nav-badge--dot merchant-nav-badge--${badge.tone}`}
        aria-label={badge.label}
      />
    );
  }
  const text = merchantBadgeText(badge);
  if (!text) return null;
  return (
    <span
      className={`merchant-nav-badge merchant-nav-badge--${badge.tone}`}
      aria-label={badge.label}
      title={badge.label}
    >
      {text}
    </span>
  );
}

export function MerchantNavKey({
  item,
  label,
  active,
  badge,
  orientation = "vertical",
  compact = false,
  onNavigate,
}: MerchantNavKeyProps) {
  const accessibleLabel = badge ? `${label}. ${badge.label}` : label;
  const target = useSupportNavigationTarget(item.to);

  return (
    <Link
      to={target}
      aria-label={accessibleLabel}
      aria-current={active ? "page" : undefined}
      className={`merchant-nav-key merchant-nav-key--${orientation}${merchantBadgeText(badge) ? " merchant-nav-key--has-count-badge" : ""}${compact ? " merchant-nav-key--compact" : ""}${item.variant ? ` merchant-nav-key--${item.variant}` : ""}`}
      style={merchantBadgeStyle(badge)}
      title={compact || orientation === "horizontal" ? label : undefined}
      onClick={onNavigate}
      onPointerEnter={() => prefetchMerchantRoute(item.id)}
      onPointerDown={() => prefetchMerchantRoute(item.id)}
      onFocus={() => prefetchMerchantRoute(item.id)}
    >
      <span
        className="material-symbols-outlined merchant-nav-key__icon"
        style={
          active
            ? { fontVariationSettings: "'FILL' 1, 'wght' 500, 'GRAD' 0, 'opsz' 24" }
            : undefined
        }
        aria-hidden="true"
      >
        {item.icon}
      </span>
      <span className="merchant-nav-key__label">{label}</span>
      <MerchantBadge badge={badge} />
    </Link>
  );
}
